import { Coffee } from "lucide-preact";

export const About1 = () => (
  <section class="flex items-center justify-center">
    <div class="text-center">
      <h1 class="text-4xl md:text-7xl font-bold mb-6 text-gray-900 dark:text-gray-100 flex items-center justify-center gap-2">!tldr</h1>
      <p class="text-xl md:text-2xl text-gray-600 dark:text-gray-400 mb-4 font-light italic">not too long; did read</p>
      <p class="text-xl md:text-xl text-gray-700 dark:text-gray-300 leading-relaxed">We're replacing mindless scrolling with something worth your time.</p>
    </div>
  </section>
);

export const About2 = () => (
  <section class="mt-10">
    <h2 class="text-4xl md:text-5xl font-bold mb-8 text-gray-900 dark:text-white">The Problem</h2>
    <div class="space-y-6 text-lg md:text-xl text-gray-700 dark:text-gray-300 leading-relaxed">
      <p>
        Every day, humanity spends billions of hours scrolling through digital content. For many of us, this becomes an endless loop of outrage, anxiety, and
        emptiness—leaving us feeling worse, not better.
      </p>
      <p>
        We're mindful of this habit. We don't like it. But it's incredibly hard to break because there isn't an easy alternative that offers genuine value
        without demanding a 30-minute commitment.
      </p>
      <p class="text-gray-600 dark:text-gray-200 font-medium">
        But scrolling isn't the enemy. Curiosity isn't the problem. The tragedy is that algorithms hijack this beautiful human instinct to serve engagement
        metrics instead of genuine substance.
      </p>
    </div>
  </section>
);

export const About3 = () => (
  <>
    <section class="mt-10">
      <h2 class="text-4xl md:text-5xl font-bold mb-8 text-gray-900 dark:text-white">Our Vision</h2>
      <div class="space-y-6 text-lg md:text-xl text-gray-700 dark:text-gray-300 leading-relaxed">
        <p>
          Imagine opening your phone and finding something interesting instead of frustrating. Content designed for coffee breaks and commutes that that's
          actually worth finishing.
        </p>
        <p>
          We're not trying to get people to scroll less. We're helping those who want to make their scrolling time feel worthwhile—ensuring that when you do
          scroll, every swipe leaves you feeling good, not agitated.
        </p>
      </div>
    </section>

    <section class="my-20">
      <a href="/tip" class="block p-6 rounded-lg text-center text-lg text-white mt-8 bg-indigo-600 hover:bg-indigo-700">
        <span class="block mb-3">Actually...</span>
        <span class="block mb-3">!tldr is built and maintained by a solo developer.</span>
        <span class="font-bold flex items-center justify-center">
          <Coffee class="w-6 h-6 inline-block mr-2" strokeWidth={3} />
          Tip me!
        </span>
      </a>
    </section>
  </>
);

export const About4 = () => <h2 class="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white">How It Works</h2>;

export const About5 = () => (
  <div class="bg-white dark:bg-neutral-900 rounded-2xl py-8 shadow-sm">
    <h3 class="text-2xl font-bold mb-4 text-gray-600 dark:text-gray-200">Made for mobile</h3>
    <p class="text-lg text-gray-700 dark:text-gray-300 mb-4">Every !tldr article follows our signature structure designed for reading on a phone:</p>
    <ul class="space-y-3 text-gray-700 dark:text-gray-300">
      <li class="flex items-start">
        <span class="text-gray-600 dark:text-gray-200 font-bold mr-3">→</span>
        <span>5-bullet summary for quick scanning</span>
      </li>
      <li class="flex items-start">
        <span class="text-gray-600 dark:text-gray-200 font-bold mr-3">→</span>
        <span>Introduction that hooks your curiosity</span>
      </li>
      <li class="flex items-start">
        <span class="text-gray-600 dark:text-gray-200 font-bold mr-3">→</span>
        <span>3 key points, each ending with a takeaway</span>
      </li>
      <li class="flex items-start">
        <span class="text-gray-600 dark:text-gray-200 font-bold mr-3">→</span>
        <span>A short conclusion that ties it together</span>
      </li>
    </ul>
  </div>
);

export const About6 = () => (
  <>
    <div class="bg-white dark:bg-neutral-900 rounded-2xl py-8 shadow-sm">
      <h3 class="text-2xl font-bold mb-4 text-gray-600 dark:text-gray-200">AI authors with distinct voices</h3>
      <p class="text-lg text-gray-700 dark:text-gray-300 mb-4">
        Our AI synthesizes insights in ways no human curator could match at scale. Unlike other platforms that summarize existing content, we generate original
        explanations that make complex ideas easy to read.
      </p>
      <p class="text-lg text-gray-700 dark:text-gray-300">
        Each article features distinct author personas—neuroscientists, philosophers, historians, practitioners—each bringing consistent voice and unique
        perspective to their topics. Think of it as having a diverse panel of experts at your fingertips, available whenever curiosity strikes.
      </p>
    </div>

    <div class="bg-white dark:bg-neutral-900 rounded-2xl py-8 shadow-sm">
      <h3 class="text-2xl font-bold mb-4 text-gray-600 dark:text-gray-200">Timeless Over Trending</h3>
      <p class="text-lg text-gray-700 dark:text-gray-300">
        We focus on concepts and insights that stay interesting over time, not temporary content or trending news. Why we procrastinate over who said what
        today. How habits form over which habits are trending. Human nature over hot takes.
      </p>
    </div>
  </>
);

export const About7 = () => (
  <div class="bg-white dark:bg-neutral-900 rounded-2xl py-8 shadow-sm">
    <h3 class="text-2xl font-bold mb-4 text-gray-600 dark:text-gray-200">Not Too Long; Did Read</h3>
    <p class="text-lg text-gray-700 dark:text-gray-300 mb-4">
      We've found the sweet spot between superficial TL;DR summaries and overwhelming deep dives. Each article is substantial enough to say something real, yet
      concise enough to finish during your commute.
    </p>
    <p class="text-lg text-gray-700 dark:text-gray-300">
      This is content you'll actually finish—not another tab you'll bookmark and never revisit. The name says it all: not too long; did read.
    </p>
  </div>
);

export const About8 = () => (
  <div>
    <About5 />
    <About6 />
    <About7 />
  </div>
);

export const About9 = () => (
  <section class="mt-10">
    <About4 />
    <About8 />
  </section>
);

export const About10 = () => <h2 class="text-2xl md:text-5xl font-bold mb-8 text-gray-900 dark:text-white">Why Now?</h2>;
